/**
 * The read path hook. Returns { data, isLoading, error, refetch }.
 *
 * There is no server to cache, so this is deliberately small: no query client,
 * no stale time, no retry policy. What it does guarantee is that every screen
 * has a real loading state to narrate, because latency.ts makes the delay real.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncResult<T> {
  data: T | undefined;
  isLoading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

export function useAsync<T>(run: () => Promise<T>, deps: unknown[] = []): AsyncResult<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const runRef = useRef(run);
  runRef.current = run;

  const key = JSON.stringify(deps);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(undefined);

    runRef
      .current()
      .then((value) => {
        if (cancelled) return;
        setData(value);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause : new Error(String(cause)));
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key, nonce]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return { data, isLoading, error, refetch };
}
